import { useEffect } from 'react'
import {
  classifyLink,
  initMetaPixel,
  trackCustomEvent,
  trackEvent,
} from '../utils/metaPixel'

type UseMetaPixelOptions = {
  /** Title of the guide/article reported with ViewContent. */
  contentName: string
  contentType?: string
}

/**
 * Installs the Meta Pixel globally and wires the guide's tracked events:
 * PageView, ViewContent, ScrollDepth (custom, 75%), Contact, InitiateCheckout and Lead.
 * Purely behavioural — it never touches layout or styling.
 */
export function useMetaPixel({
  contentName,
  contentType = 'guide',
}: UseMetaPixelOptions): void {
  useEffect(() => {
    initMetaPixel()
    trackEvent('PageView')
    trackEvent('ViewContent', {
      content_name: contentName,
      content_type: contentType,
    })
  }, [contentName, contentType])

  // ScrollDepth — fires once at ~75% of the page.
  useEffect(() => {
    let fired = false

    const onScroll = () => {
      if (fired) return
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - window.innerHeight
      if (scrollable <= 0) return
      const progress = (window.scrollY || doc.scrollTop) / scrollable
      if (progress >= 0.75) {
        fired = true
        trackCustomEvent('ScrollDepth', {
          percent: 75,
          content_name: contentName,
        })
        window.removeEventListener('scroll', onScroll)
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [contentName])

  // Outbound CTA clicks — Contact, InitiateCheckout and Lead.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!anchor) return

      const intent = classifyLink(anchor.getAttribute('href') ?? '')
      if (!intent) return

      const label = (anchor.textContent ?? '').trim().slice(0, 80)

      if (intent === 'contact') {
        trackEvent('Contact', { content_name: label, method: 'whatsapp' })
      } else if (intent === 'checkout') {
        trackEvent('InitiateCheckout', {
          content_name: label,
          content_category: 'ringana_webshop',
        })
      } else {
        trackEvent('Lead', {
          content_name: label,
          content_category: 'skin_fresh_index',
        })
      }
    }

    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])
}
